import type { V5TourDefinition } from "./v5TourTypes";

/**
 * The two Vertical demo tours (VERTICAL_DEMOS_V2 §5). Anchors are
 * `data-tour="v5-*"` attributes on the live V5 surfaces/widgets; steps without
 * an element render as centered narration. Every claim in the copy is backed
 * by the seeded V3 data (real AVL grids, real Flow360 cases) — no slideware.
 */

export const CFD_TRACEBACK_TOUR: V5TourDefinition = {
  id: "cfd-traceback",
  title: "Chart → geometry traceback",
  // stage-ready: the run table has rows and the CL–α chart has real points
  readySelectors: [
    '[data-tour="v5-table-widget"] .ag-row',
    '[data-tour="v5-plot-widget"] .js-plotly-plot .scatterlayer .point',
  ],
  steps: [
    {
      popover: {
        title: "This is Thread — not a slide deck",
        description:
          "You are inside the live product, anonymously, on a real project: " +
          "623 Flow360 RANS cases of the NASA X-57, imported with full " +
          "provenance. Everything you are about to click is a real record.",
      },
      route: { view: "workbench", dashboard: "cfd-traceback-v2" },
      dwellMs: 8000,
    },
    {
      element: '[data-tour="v5-table-widget"]',
      popover: {
        title: "Every run is an atomic record",
        description:
          "Each row is one CFD case — an OperationRun with its solver config, " +
          "operating condition and coefficients bound as typed inputs and " +
          "outputs. Nothing here was typed into a spreadsheet.",
        side: "top",
      },
      dwellMs: 9000,
    },
    {
      element: '[data-tour="v5-plot-widget"]',
      popover: {
        title: "The chart is data, not a picture",
        description:
          "This CL–α sweep is rendered from plot records whose every point " +
          "carries the identity of the case that produced it.",
        side: "right",
      },
      challenge: {
        instruction: "Click any point on the chart to select its case.",
        validate: (live) => live.selectedRunId !== null,
      },
      dwellMs: 9000,
    },
    {
      element: '[data-tour="v5-dag-card"]',
      popover: {
        title: "…and every point tracebacks to geometry",
        description:
          "The graph re-roots on your selection: coefficient → solve → volume " +
          "mesh → surface mesh → geometry. Each hop is a typed binding on a " +
          "content-hashed version — the audit trail a certification reviewer " +
          "actually wants.",
        side: "left",
      },
      dwellMs: 10000,
    },
    {
      popover: {
        title: "The whole campaign is a graph",
        description:
          "The lineage you just traced is one cone of the project DAG — " +
          "explore it full-screen. The Scale view holds its shape from " +
          "hundreds of cases to a million nodes; this is a substrate, not a " +
          "viewer.",
      },
      route: { focus: "dag" },
      dwellMs: 10000,
    },
    {
      element: '[data-tour="v5-ask-chart"]',
      popover: {
        title: "Ask the database, not the chart",
        description:
          "Query an off-sample condition and the model answers with an " +
          "uncertainty — σ grows exactly where the campaign is thin. The " +
          "answer knows where the data isn't.",
        side: "right",
      },
      route: { focus: null, dashboard: "cfd-traceback-v2" },
      dwellMs: 10000,
    },
    {
      element: '[data-tour="v5-plot-recall"]',
      popover: {
        title: "Recover a screenshot to its source",
        description:
          "Drop a degraded screenshot of a plot — Thread fingerprints it and " +
          "recovers the exact rendered-plot artifact, with its full lineage. " +
          "No more 'which run made this figure?'",
        side: "right",
      },
      dwellMs: 9000,
    },
    {
      popover: {
        title: "And it keeps moving",
        description:
          "The β=0 spine of this sweep was genuinely missing — so we ran it. " +
          "Eight new Flow360 cases forked on the same mesh land in this graph " +
          "as they converge, with the same provenance as every other row.",
      },
      dwellMs: 9000,
    },
  ],
};

export const SC_COVERAGE_TOUR: V5TourDefinition = {
  id: "sc-coverage",
  title: "S&C coverage & handling qualities",
  // stage-ready: coverage map + HQ boundary charts drawn, grid rows in
  readySelectors: [
    '[data-tour="v5-coverage-map"] svg',
    '[data-tour="v5-hq-margins"] svg',
    '[data-tour="v5-table-widget"] .ag-row',
  ],
  steps: [
    {
      popover: {
        title: "An aero database that knows its own limits",
        description:
          "A lift+cruise eVTOL in wing-borne cruise: a dense α × β × elevator " +
          "database solved in AVL — 1,669 real vortex-lattice runs, holes and " +
          "all, fused with empirical and aeroelastic estimates. Every " +
          "derivative is traceable to the solve that produced it.",
      },
      route: { view: "workbench", dashboard: "sc-coverage-v2" },
      dwellMs: 9000,
    },
    {
      element: '[data-tour="v5-coverage-map"]',
      popover: {
        title: '"Tell me where I\'m extrapolating"',
        description:
          "The envelope hull and the thin patches are computed from the " +
          "database itself. Interior, on-hull, extrapolated — with a thinness " +
          "score, not a shrug.",
        side: "right",
      },
      challenge: {
        instruction:
          "Click on the map — try inside the envelope, then out beyond it.",
        validate: () =>
          document.querySelector('[data-tour="v5-coverage-map"] .v5-coverage-verdict') !== null,
      },
      dwellMs: 11000,
    },
    {
      element: '[data-tour="v5-coverage-map"]',
      popover: {
        title: "Where to sample next",
        description:
          "The inverse query ranks the worst-covered candidates — the corners " +
          "the campaign never flew and the mid-envelope patch solved only on " +
          "the coarse pass. A test plan, derived from the data.",
        side: "right",
      },
      dwellMs: 9000,
    },
    {
      element: '[data-tour="v5-hq-margins"]',
      popover: {
        title: "Dutch roll, on the boundary",
        description:
          "Modes come from the fused derivatives through a state-space model. " +
          "This aircraft holds Level 1 Dutch roll by a damping margin of " +
          "five thousandths — and the locus shows what happens next.",
        side: "left",
      },
      dwellMs: 11000,
    },
    {
      element: '[data-tour="v5-hq-margins"]',
      popover: {
        title: "One packaging decision from Level 2",
        description:
          "Move ten percent of the battery into the lift booms and the yaw " +
          "inertia takes Dutch roll out of Level 1. Every point on that locus " +
          "is a full analysis run, traceable to its inputs — this is how a " +
          "requirement argues with a design.",
        side: "left",
      },
      dwellMs: 12000,
    },
    {
      element: '[data-tour="v5-plot-widget"]',
      popover: {
        title: "Time histories, honestly labelled",
        description:
          "A rudder doublet rings the Dutch roll (linear, small-signal — it " +
          "says so). The elevator doublet runs the full nonlinear equations: " +
          "agreement at 1°, 11% divergence at 6°. The model's validity is part " +
          "of the record.",
        side: "right",
      },
      dwellMs: 11000,
    },
    {
      element: '[data-tour="v5-table-widget"]',
      popover: {
        title: "The grid behind everything",
        description:
          "All 1,669 solved points, as first-class runs. Select one and its " +
          "provenance trail rebuilds — the same traceback discipline as the " +
          "CFD campaign, because it is the same substrate.",
        side: "top",
      },
      challenge: {
        instruction: "Select any run in the table.",
        validate: (live) => live.selectedRunId !== null,
      },
      dwellMs: 9000,
    },
    {
      popover: {
        title: "Reference quantities are records too",
        description:
          "Mass, inertias, areas — declared once as first-class artifacts, " +
          "consumed by binding, never re-typed. When a number is hard-coded " +
          "somewhere downstream, Thread can tell you. That is the whole point.",
      },
      dwellMs: 10000,
    },
  ],
};

export const WING_CS_TOUR: V5TourDefinition = {
  id: "wing-cs",
  title: "Aircraft in CAD → control surface out",
  // stage-ready: the Wing screen's CAD-source input is mounted
  readySelectors: ['[data-tour="wing-cad-source"] input'],
  steps: [
    {
      popover: {
        title: "Add an aircraft in CAD — we add the control surface",
        description:
          "This is the live Wing screen working on real CAD: a one-piece " +
          "STEP of a Cessna 172. No OpenVSP, no re-modelling — the pipeline " +
          "slices, shapes and cuts the customer's own geometry.",
      },
      route: { view: "wing-section" },
      dwellMs: 8000,
    },
    {
      element: '[data-tour="wing-cad-source"]',
      popover: {
        title: "1 · Point at your CAD",
        description:
          "Paste the STEP path (or pick an uploaded geometry). From here " +
          "on, every section you see is sliced from THIS solid — real " +
          "airfoil contours, real chord and twist, not a library foil.",
        side: "bottom",
      },
      dwellMs: 9000,
    },
    {
      element: '[data-tour="wing-stations"]',
      popover: {
        title: "2 · Choose the slicing",
        description:
          "Pick where to start, where to end and how many stations — or " +
          "drag individual η chips. Outboard stations land on the aileron " +
          "span you are about to cut.",
        side: "bottom",
      },
      dwellMs: 8000,
    },
    {
      element: '[data-tour="wing-slice"]',
      popover: {
        title: "3 · Slice the wing",
        description:
          "OCC slices the STEP at each station (~15 s, cached). The " +
          "planform — LE line, chord distribution — and the hinge height " +
          "are then FITTED from these sections, never guessed from a " +
          "bounding box.",
        side: "bottom",
      },
      dwellMs: 9000,
    },
    {
      element: '[data-tour="wing-sections"]',
      popover: {
        title: "4 · Every station opens in the 2D foil tool",
        description:
          "Each sliced section seeds the full parameterised 2D Workbench " +
          "(the high-lift / section editor) - cove cuts, vane, flaperon, " +
          "deploy track. Design the " +
          "section in 2D exactly as you would a standalone airfoil; the " +
          "design hash plugs into the same solution cache.",
        side: "top",
      },
      dwellMs: 10000,
    },
    {
      element: '[data-tour="wing-cs-panel"]',
      popover: {
        title: "5 · Cut the control surface into the CAD",
        description:
          "Choose the span (η start/end), hinge x/c and mode: quick gapped " +
          "cut (box + LE radius + gap), the shaped cove + spiral-LE cut, or " +
          "your saved multi-element design (vane + cove). Deflections are " +
          "pre-rotated — one STEP per case.",
        side: "top",
      },
      dwellMs: 10000,
    },
    {
      popover: {
        title: "Flow360-ready — on the beta mesher",
        description:
          "The deflected aircraft meshes on the in-house beta mesher (never " +
          "Geometry AI — it rounds sharp trailing edges). From CAD to a " +
          "deflection sweep of meshable geometry in under a minute — with " +
          "every step recorded in Thread.",
      },
      dwellMs: 9000,
    },
  ],
};

export const V5_TOURS: Record<string, V5TourDefinition> = {
  [CFD_TRACEBACK_TOUR.id]: CFD_TRACEBACK_TOUR,
  [SC_COVERAGE_TOUR.id]: SC_COVERAGE_TOUR,
  [WING_CS_TOUR.id]: WING_CS_TOUR,
};
